import { Spinner, useToast } from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import {
  MAX_FRACTION_DIGITS,
  WrapBridgeMappings,
  isNumber,
  toFixedWithoutRounding,
} from "@constants/common";
import useDappChain from "@hooks/useDappChain";
import useDappConfig from "@hooks/useDappConfig";
import useSwapTokensBalances from "@hooks/useSwapTokensBalances";

import UTMLink from "@components/UTMLink";
import { swappingContractsConfig } from "@config/contractsConfig";
import { oftBridgePolicy } from "@config/oftBridgeConfig";
import {
  APPROVE_WRAPPING_CLICK,
  CONNECT_WALLET,
  MAX_WRAP_CLICK,
  UNWRAP_INITIATED,
  UNWRAP_FAILED,
  UNWRAP_SUCCESSFUL,
  WRAP_INITIATED,
  WRAP_FAILED,
  WRAP_SUCCESSFUL,
} from "@constants/analytics";
import {
  Button,
  Card,
  Input,
  Typography,
} from "@kelp-dao/kernel-design-system";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { FaRegCircleCheck } from "react-icons/fa6";
import { useDispatch } from "react-redux";
import { updateUnwrapTxnState } from "@store/slices/globalSlice";
import { formatNumber, isErrorRejectedByUser, trackEvent } from "utils";
import { formatUnits, parseEther } from "viem";
import {
  useAccount,
  useBalance,
  usePublicClient,
  useSwitchChain,
  useWriteContract,
} from "wagmi";
import { MAX_UNWRAP_CLICK } from "../../constants/analytics";
import { TChainSelection } from "./types";
import TokenBadge, {
  TOKEN_BADGE_CONTAINER,
} from "@components/BridgeWrapV2/TokenBadge";

// type TTicketInputRightSectionProps = {
//   token: string;
//   icon: string;
// };
// type TTicketInputBalanceProps = {
//   onClick(): void;
//   token: string;
//   tokenAmount: number;
//   isConnected: boolean;
//   hideMax?: boolean;
//   style?: ChakraProps;
// };

const txnInitialState = {
  open: false,
  hash: "",
  error: "",
  hasUserDenied: false,
};

// const TicketInputRightSection = ({
//   token,
//   icon,
// }: TTicketInputRightSectionProps) => {
//   return (
//     <Flex flexDir="column" alignItems="center">
//       <Flex gap={2} alignItems="center">
//         {icon && <BaseImage width={18} height={18} alt={token} src={icon} />}
//         <Flex alignItems="center" justify="space-between" flex={1} gap={1}>
//           <Box flex={1} fontWeight={500}>
//             {token}
//           </Box>
//         </Flex>
//       </Flex>
//     </Flex>
//   );
// };

// const TicketInputBalance = ({
//   token,
//   tokenAmount,
//   isConnected,
//   onClick,
//   hideMax = false,
//   style,
// }: TTicketInputBalanceProps) => {
//   return (
//     <Flex
//       alignItems="baseline"
//       justifyContent={"end"}
//       gap="6px"
//       mt="4px"
//       {...style}
//     >
//       {isConnected && tokenAmount !== undefined && tokenAmount != null ? (
//         <Text fontWeight="500" fontSize="12px" mt="6px" color="textPrimary">
//           Balance:{" "}
//           <strong>
//             {formatNumber(Number(tokenAmount), false, 4, true)} {token}
//           </strong>
//         </Text>
//       ) : null}
//       {!hideMax && (
//         <Button variant="primary" onClick={() => onClick()}>
//           MAX
//         </Button>
//       )}
//     </Flex>
//   );
// };

const WrapUnwrapAccordianV2 = ({
  type,
  sourceChain,
  destinationChain,
}: {
  type: string;
  sourceChain: TChainSelection;
  destinationChain: TChainSelection;
}) => {
  const { chain: wagmiChain } = useAccount();
  const queryClient = useQueryClient();
  const { switchChain } = useSwitchChain();
  const { chain } = useDappChain();
  const chainIdParam = type === "wrap" ? destinationChain.id : sourceChain.id;
  const { swapConfig } = useDappConfig();
  const { isConnected } = useAccount();
  const client = usePublicClient();
  const dispatch = useDispatch();

  const { rsETH = null, wrsETH = null } =
    isConnected && chainIdParam && swappingContractsConfig[chainIdParam]?.tokens
      ? swappingContractsConfig[chainIdParam].tokens
      : {};

  const fromToken = type === "wrap" ? rsETH : wrsETH;
  const [swapAmount, setSwapAmount] = useState("");
  const [error, setError] = useState("");
  const [isPending, setPending] = useState(false);
  const [txnCompleted, setTxnCompleted] = useState(false);
  const { writeContract } = useWriteContract();
  const { openConnectModal } = useConnectModal();
  const toast = useToast();

  const hasToSwitch =
    type === "wrap"
      ? destinationChain?.id !== chain.id
      : sourceChain?.id !== chain.id;

  const { data, queryKey } = useBalance({
    token: rsETH?.address,
    address: wrsETH?.address,
    chainId: chainIdParam,
    query: {
      refetchInterval: oftBridgePolicy.balanceRefetchMs,
    },
  });

  const unwrapLimit =
    data && rsETH?.decimals
      ? Number(formatUnits(data.value, rsETH.decimals))
      : 0;

  const rsEthSelected = rsETH?.symbol === fromToken?.symbol;

  const [isTxModalOpen, setIsTxModalOpen] = useState(txnInitialState);
  const {
    rsETHBalance,
    rsETHAllowance,
    wrsETHBalance,
    wrsETHAllowance,
    invalidateCache,
  } = useSwapTokensBalances(chainIdParam);

  const fromTokenBal = formatUnits(
    rsEthSelected ? rsETHBalance : wrsETHBalance,
    18
  );

  const toTokenBal = formatUnits(
    rsEthSelected ? wrsETHBalance : rsETHBalance,
    18
  );

  // Widen to 6 decimals below 0.0001 so small holdings don't read as "0".
  const fromBalanceAmount = Number(fromTokenBal);
  const fromBalanceFractionDigits = fromBalanceAmount < 0.0001 ? 6 : 4;

  const toBalanceAmount = Number(toTokenBal);
  const toBalanceFractionDigits = toBalanceAmount < 0.0001 ? 6 : 4;

  const tokenAllowance = formatUnits(
    rsEthSelected ? rsETHAllowance : wrsETHAllowance,
    18
  );

  const hasSufficientAllowance =
    !rsEthSelected || Number(tokenAllowance) >= Number(swapAmount);

  const toToken = rsEthSelected ? wrsETH : rsETH;

  const chainIdForSwitch =
    type === "wrap" ? destinationChain.id : sourceChain?.id;

  const hasBalance = Number(toFixedWithoutRounding(fromTokenBal, 4)) > 0;

  const checkInput = useCallback(
    (value = swapAmount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > Number(fromTokenBal)) {
        error = `You can't wrap/unwrap more than available balance in your wallet.`;
      } else if (!rsEthSelected && Number(value) > Number(unwrapLimit)) {
        error = `You can't unwrap more than available limit of ${unwrapLimit} ${fromToken?.symbol}.`;
      }
      return error;
    },
    [
      swapAmount,
      isConnected,
      fromTokenBal,
      rsEthSelected,
      unwrapLimit,
      fromToken?.symbol,
    ]
  );

  useEffect(() => {
    if (swapAmount !== "") {
      const error = checkInput(swapAmount);
      setError(error);
    }
  }, [checkInput, swapAmount, fromTokenBal]);

  useEffect(() => {
    if (txnCompleted) {
      setTxnCompleted(false);
    }
  }, [chain, sourceChain, destinationChain]);

  const handleApprove = () => {
    if (!client || !fromToken || !wrsETH) {
      return;
    }

    setIsTxModalOpen({
      open: true,
      hash: "",
      error: "",
      hasUserDenied: false,
    });

    setPending(true);

    writeContract(
      {
        address: fromToken.address,
        abi: fromToken.abi,
        functionName: "approve",
        args: [wrsETH.address, parseEther(swapAmount)],
      },
      {
        onError: (error) => {
          trackEvent(APPROVE_WRAPPING_CLICK, {
            amount: swapAmount,
            source_chain: sourceChain.title || "",
            destination_chain: destinationChain.title || "",
            chainId: String(chainIdParam),
            error: String(error),
            dapp: "kelp",
          });
          setPending(false);
          setIsTxModalOpen({
            open: true,
            // @ts-ignore
            hash: error.transactionHash,
            // @ts-ignore
            error: error?.reason ?? error?.error ?? error?.message ?? "",
            hasUserDenied: isErrorRejectedByUser(error),
          });
        },
        onSuccess: async (hash) => {
          trackEvent(APPROVE_WRAPPING_CLICK, {
            amount: swapAmount,
            source_chain: sourceChain.title || "",
            destination_chain: destinationChain.title || "",
            chainId: String(chainIdParam),
            rsETH_Balance: formatUnits(rsETHBalance, 18),
            wrsETH_Balance: formatUnits(wrsETHBalance, 18),
            dapp: "kelp",
          });
          setIsTxModalOpen({
            open: true,
            hash,
            error: "",
            hasUserDenied: false,
          });
          await client.waitForTransactionReceipt({
            hash,
          });
          invalidateCache();
          queryClient.invalidateQueries({ queryKey });
          setPending(false);
        },
      }
    );
  };

  const handleSwap = () => {
    if (!client || !fromToken || !rsETH || !wrsETH) {
      return;
    }
    const error = checkInput(swapAmount);
    if (error) {
      setError(error);
      return;
    }

    if (wagmiChain?.id !== swapConfig.chain.id) {
      toast({
        description: "chain and config mismatch. Please refresh",
        status: "error",
        duration: 5000,
        position: "top",
      });
      // eslint-disable-next-line no-console
      console.log(`chain and config mismatch. Please refresh`);
      return;
    }

    setIsTxModalOpen({
      open: true,
      hash: "",
      error: "",
      hasUserDenied: false,
    });
    setPending(true);
    const functionName =
      fromToken.symbol === rsETH.symbol ? "deposit" : "withdraw";

    // Track wrap/unwrap_initiated event
    const isWrap = type === "wrap";
    trackEvent(isWrap ? WRAP_INITIATED : UNWRAP_INITIATED, {
      source_chain: sourceChain.title || "",
      destination_chain: destinationChain.title || "",
      source_token_symbol: isWrap
        ? fromToken?.symbol || ""
        : toToken?.symbol || "",
      destination_token_symbol: isWrap
        ? toToken?.symbol || ""
        : fromToken?.symbol || "",
      amount: swapAmount,
      from_chain_id: String(sourceChain.id),
      to_chain_id: String(destinationChain.id),
      dapp: "kelp",
    });

    writeContract(
      {
        address: wrsETH.address,
        abi: wrsETH.abi,
        functionName,
        args: [rsETH.address, parseEther(swapAmount)],
      },
      {
        onError: (error) => {
          setPending(false);
          setIsTxModalOpen({
            open: true,
            // @ts-ignore
            hash: error.transactionHash,
            // @ts-ignore
            error: error?.reason ?? error?.error ?? error?.message ?? "",
            hasUserDenied: isErrorRejectedByUser(error),
          });
          setTxnCompleted(true);

          // Track wrap/unwrap_failed event
          trackEvent(isWrap ? WRAP_FAILED : UNWRAP_FAILED, {
            source_chain: sourceChain.title || "",
            destination_chain: destinationChain.title || "",
            source_token_symbol: isWrap
              ? fromToken?.symbol || ""
              : toToken?.symbol || "",
            destination_token_symbol: isWrap
              ? toToken?.symbol || ""
              : fromToken?.symbol || "",
            amount: swapAmount,
            from_chain_id: String(sourceChain.id),
            to_chain_id: String(destinationChain.id),
            error_reason: error?.message || String(error),
            dapp: "kelp",
          });
        },
        onSuccess: async (hash) => {
          setIsTxModalOpen({
            open: true,
            hash,
            error: "",
            hasUserDenied: false,
          });
          await client.waitForTransactionReceipt({
            hash,
          });
          invalidateCache();
          setPending(false);
          queryClient.invalidateQueries({ queryKey });
          setSwapAmount("");
          setTxnCompleted(true);
          if (!isWrap) {
            dispatch(
              updateUnwrapTxnState({
                success: true,
              })
            );
          }

          // Track wrap/unwrap_successful event
          trackEvent(isWrap ? WRAP_SUCCESSFUL : UNWRAP_SUCCESSFUL, {
            source_chain: swapConfig.chain.name || "",
            destination_chain: swapConfig.chain.name || "",
            source_token_symbol: isWrap
              ? fromToken?.symbol || ""
              : toToken?.symbol || "",
            destination_token_symbol: isWrap
              ? toToken?.symbol || ""
              : fromToken?.symbol || "",
            amount: swapAmount,
            from_chain_id: String(swapConfig.chain.id),
            to_chain_id: String(swapConfig.chain.id),
            hash,
            dapp: "kelp",
          });
        },
      }
    );
  };

  const handleMaxClick = () => {
    const ev = type === "wrap" ? MAX_WRAP_CLICK : MAX_UNWRAP_CLICK;
    trackEvent(ev, {
      rsETH_Balance: formatUnits(rsETHBalance, 18),
      wrsETH_Balance: formatUnits(wrsETHBalance, 18),
      dapp: "kelp",
    });
    setSwapAmount(toFixedWithoutRounding(fromTokenBal, 4));
  };

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setSwapAmount(val);
      } else {
        if (txnCompleted) setTxnCompleted(false);
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setSwapAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const handleConnectWallet = () => {
    openConnectModal?.();
    trackEvent(CONNECT_WALLET, {
      type: "wrap_page",
      dapp: "kelp",
    });
  };

  const getButtonText = () => {
    if (!isConnected) return "Connect Wallet";
    if (hasToSwitch)
      return `Switch to ${
        type === "wrap" ? destinationChain.title : sourceChain.title
      }`;
    if (!hasSufficientAllowance) return isPending ? "Approving" : "Approve";
    if (rsEthSelected) return isPending ? "Wrapping Token" : "Wrap Token";
    return isPending ? "Unwrapping Token" : "Unwrap Token";
  };

  const handleButtonClick = () => {
    if (!isConnected) {
      handleConnectWallet();
    } else if (hasToSwitch) {
      switchChain({ chainId: Number(chainIdForSwitch) });
    } else if (!hasSufficientAllowance) {
      handleApprove();
    } else {
      handleSwap();
    }
  };

  if (!fromToken || !toToken) {
    return null;
  }

  return (
    <>
      <Card
        variant="non-bordered"
        className="w-full p-4 md:p-8 flex flex-col gap-4 rounded-2xl"
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-1">
            <BaseImage
              src={
                WrapBridgeMappings.ICONS[chainIdParam] ||
                WrapBridgeMappings.ICONS.placeholder
              }
              alt={type === "wrap" ? destinationChain.title : sourceChain.title}
              width={18}
              height={18}
            />
            <Typography
              variant="xsmall"
              type="body"
              className="text-text-secondary"
            >
              {type === "wrap" ? destinationChain.title : sourceChain.title}
            </Typography>
          </div>
        </div>
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div className="flex flex-col gap-1 w-full">
            <Typography
              variant="xsmall"
              type="body"
              className="text-text-secondary"
            >
              From
            </Typography>
            <Input
              value={swapAmount}
              onChange={(e) => handleChange(e.target.value)}
              containerClassName="w-full"
              rightElementClassName={TOKEN_BADGE_CONTAINER}
              placeholder="Enter Amount"
              rightElement={<TokenBadge symbol={fromToken.symbol} />}
            />
            <div className="flex justify-between items-center gap-2 h-[40px]">
              <Typography
                variant="xsmall"
                type="body"
                className="text-text-secondary"
              >
                Balance:{" "}
                <strong>
                  {formatNumber(
                    fromBalanceAmount,
                    false,
                    fromBalanceFractionDigits,
                    true
                  )}{" "}
                  {fromToken.symbol}
                </strong>
              </Typography>
              <Button variant="outline" size="xsmall" onClick={handleMaxClick}>
                MAX
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-1 w-full">
            <Typography
              variant="xsmall"
              type="body"
              className="text-text-secondary"
            >
              To
            </Typography>
            <Input
              value={swapAmount}
              onChange={() => {}}
              disabled
              containerClassName="w-full"
              inputClassName="bg-[#f4f4f7] opacity-100"
              rightElementClassName={TOKEN_BADGE_CONTAINER}
              rightElement={<TokenBadge symbol={toToken.symbol} />}
            />
            <div className="flex justify-between items-center gap-2 h-[40px]">
              <Typography
                variant="xsmall"
                type="body"
                className="text-text-secondary"
              >
                Balance:{" "}
                <strong>
                  {formatNumber(
                    toBalanceAmount,
                    false,
                    toBalanceFractionDigits,
                    true
                  )}{" "}
                  {toToken.symbol}
                </strong>
              </Typography>
            </div>
          </div>
        </div>
        {!txnCompleted ? (
          <Button
            variant="primary"
            disabled={!!error || isPending || (!hasToSwitch && !swapAmount)}
            onClick={handleButtonClick}
          >
            {isPending && <Spinner size="sm" color="white" />}
            {getButtonText()}
          </Button>
        ) : (
          <>
            <Button variant="primary">
              {!isTxModalOpen.error && <FaRegCircleCheck color="#fff" />}

              {isTxModalOpen.error
                ? "TRANSACTION FAILED"
                : rsEthSelected
                ? "WRAPPED SUCCESSFULLY"
                : "UNWRAPPED SUCCESSFULLY"}
            </Button>

            <div
              className={`text-text-secondary text-xs ${
                isTxModalOpen.error ? "text-error" : ""
              }`}
            >
              {isTxModalOpen.error ? (
                <div className="flex gap-1">
                  <Typography
                    variant="xsmall"
                    type="body"
                    borderBottom="1px solid"
                    borderColor="#FF616A"
                    onClick={() => {
                      setIsTxModalOpen(txnInitialState);
                      setTxnCompleted(false);
                    }}
                    className="cursor-pointer"
                  >
                    Click here
                  </Typography>
                  <Typography variant="xsmall" type="body">
                    to reset & try again
                  </Typography>
                </div>
              ) : (
                <UTMLink
                  isExternal
                  borderBottom="1px solid"
                  borderColor="textSecondary"
                  onClick={() => {
                    const url = `${chain.blockExplorers?.default.url}/tx/${isTxModalOpen.hash}`;
                    window.open(url, "_blank");
                  }}
                >
                  View Transaction
                </UTMLink>
              )}
            </div>
          </>
        )}

        {!rsEthSelected && (
          <div className="flex justify-start gap-1 w-full">
            <div className="flex gap-1 items-center">
              <Typography
                variant="xsmall"
                type="body"
                className="text-text-secondary"
              >
                Unwrap Limit:
              </Typography>
            </div>
            <Typography
              variant="xsmall"
              type="body"
              className="text-text-secondary"
            >
              {+unwrapLimit.toFixed(4)}
            </Typography>
          </div>
        )}

        {error && !txnCompleted && (
          <div className="flex justify-center items-center">
            <BaseImage
              src="/assets/transactions/error.svg"
              width={20}
              height={20}
              alt="error-icon"
            />
            <Typography
              variant="xsmall"
              type="body"
              className="text-feedback-error leading-[12px]"
            >
              {error ? error : "error"}
            </Typography>
          </div>
        )}

        {!hasBalance && !error && !txnCompleted && (
          <div className="flex justify-center items-center">
            <BaseImage
              src="/assets/transactions/error.svg"
              width={20}
              height={20}
              alt="error-icon"
            />
            <Typography
              variant="xsmall"
              type="body"
              className="text-feedback-error leading-[12px]"
            >
              Not enough balance for this transaction
            </Typography>
          </div>
        )}
      </Card>
    </>
  );
};

export default WrapUnwrapAccordianV2;
